import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Card, ListGroup, Spinner, Alert, Button,
  Row, Col, Accordion,
} from 'react-bootstrap';
import { API_URL } from '../config';
import { readerFetch } from '../apiAuth';
import './MyCabinet.css';
import { t } from '../i18n';
import PaginationBlock from './PaginationBlock';
import OrderModal from './OrderModal';
import CoverArt from './CoverArt';
import { safeExtUrl as _safeExtUrl } from '../safeUrl';

// ─────────────────────────────────────────
// Сховище даних гостя — тільки sessionStorage.
// Дані живуть рівно одну сесію браузера:
// при закритті вкладки, повторному вході або зміні пристрою — скидаються автоматично.
// ─────────────────────────────────────────

const GUEST_LOGIN = '__guest__';

// Внутрішній скрол списків кабінету, коли елементів більше порогу (сесія
// 2026-08-12) — щоб дуже активний читач не розтягував основну сторінку в
// довгий скрол. Спільні для «Книги на руках», «Закладки», «Повні тексти
// видання», «Замовлення» (SectionOrders має власний renderOrderList, та
// сама логіка й той самий CSS-клас). Обгортку додаємо лише коли елементів
// СПРАВДІ більше порогу — тому рівно на порозі скрол ніколи не з'являється,
// незалежно від висоти вмісту рядків (вона відрізняється між розділами).
const CABINET_LIST_SCROLL_THRESHOLD = 5;

function scrollWrapIfLong(list, node) {
  return list.length > CABINET_LIST_SCROLL_THRESHOLD
    ? <div className="cabinet-list-scroll-wrap">{node}</div>
    : node;
}

// ─── Обкладинки: надійне перемикання на заглушку ──────────────
// Заглушка вмикається вручну через style (display), тому React про
// неї не знає. Списки з key={індекс} перевикористовують ті самі
// DOM-вузли (зміна місяця в «Історії», видалення закладки) — і
// приховане від попередньої книги <img> лишалось прихованим для
// наступної. Тому обгортку обкладинки ключуємо за cover_url (вузли
// створюються заново). ref-колбек — додаткова страховка: ловить
// картинку, що вже завершилась помилкою на момент монтування.
function _coverFail(node) {
  if (!node) return;
  node.style.display = 'none';
  let sib = node.nextSibling;
  while (sib && sib.nodeType !== 1) sib = sib.nextSibling; // пропускаємо текстові вузли
  if (sib) sib.style.display = 'flex';
}
function coverImgError(e) { _coverFail(e.target); }
function coverImgRef(node) {
  if (node && node.complete && node.naturalWidth === 0) _coverFail(node);
}

function _getGuestData(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) || []) : [];
  } catch { return []; }
}

function _saveGuestData(key, items) {
  try { sessionStorage.setItem(key, JSON.stringify(items)); } catch {}
}

// Читач — localStorage (постійно між сесіями), гість — sessionStorage.
// Обидва об'єкти мають однаковий API (getItem/setItem/removeItem) —
// раніше ця гілка дублювалась окремо в кожному з місць, що читають/пишуть
// запити й повні тексти (на відміну від закладок — ті вже йдуть через MySQL).
function _storageFor(login) {
  return login === GUEST_LOGIN ? sessionStorage : localStorage;
}

// Форматування дати з MySQL (added_at, "YYYY-MM-DD HH:MM:SS") у той самий
// вигляд, що й addedAt гостя ("DD.MM.YYYY" через toLocaleDateString).
function _formatAddedAt(mysqlDatetime) {
  if (!mysqlDatetime) return '';
  const d = new Date(mysqlDatetime.replace(' ', 'T'));
  return isNaN(d) ? '' : d.toLocaleDateString('uk-UA');
}

// Публічні хелпери (використовуються і в BookFullView та App)
// Гість — sessionStorage (немає постійної особи між пристроями).
// Зареєстрований читач — MySQL через bookmarks.php (user.reader_token).
async function getBookmarks(login, user = null) {
  const key = `irbis_bookmarks_${login}`;
  if (login === GUEST_LOGIN) return _getGuestData(key);
  if (!user?.reader_token) return [];
  try {
    const r = await readerFetch(`${API_URL}/bookmarks.php?action=list`, user.reader_token);
    const d = await r.json();
    return (d.bookmarks || []).map(b => ({
      mfn: b.mfn,
      db: b.db,
      title: b.title || '',
      author: b.author || '',
      year: b.year || '',
      cover_url: b.cover_url || null,
      addedAt: _formatAddedAt(b.added_at),
    }));
  } catch { return []; }
}
function addBookmark(login, user, entry) {
  const key = `irbis_bookmarks_${login}`;
  if (login === GUEST_LOGIN) {
    _saveGuestData(key, [entry, ..._getGuestData(key)]);
    return;
  }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/bookmarks.php?action=add`, user.reader_token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      db: entry.db, mfn: entry.mfn, title: entry.title,
      author: entry.author, year: entry.year, cover_url: entry.cover_url,
    }),
  }).catch(() => {});
}
// Кілька закладок одним махом («Вибрані» в результатах пошуку, сесія 2026-10-25).
// Гість — локально, без дублів за парою (db, mfn); читач — один запит
// bookmarks.php?action=addMany. Promise<number> — скільки записів тепер у закладках.
async function addBookmarksMany(login, user, entries) {
  const key = `irbis_bookmarks_${login}`;
  if (login === GUEST_LOGIN) {
    const existing = _getGuestData(key);
    const has = new Set(existing.map(b => `${b.db}|${b.mfn}`));
    const addedAt = new Date().toLocaleDateString('uk-UA');
    const fresh = entries
      .filter(e => !has.has(`${e.db}|${e.mfn}`))
      .map(e => ({ ...e, addedAt }));
    _saveGuestData(key, [...fresh, ...existing]);
    return entries.length;
  }
  if (!user?.reader_token) throw new Error('no token');
  const r = await readerFetch(`${API_URL}/bookmarks.php?action=addMany`, user.reader_token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: entries.map(e => ({
        db: e.db, mfn: e.mfn, title: e.title,
        author: e.author, year: e.year, cover_url: e.cover_url,
      })),
    }),
  });
  const d = await r.json();
  if (!r.ok || !d.success) throw new Error(d.error || 'addMany failed');
  return d.added;
}
function removeBookmark(login, user, db, mfn) {
  const key = `irbis_bookmarks_${login}`;
  if (login === GUEST_LOGIN) {
    _saveGuestData(key, _getGuestData(key).filter(b => !(b.mfn === mfn && b.db === db)));
    return;
  }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/bookmarks.php?action=remove`, user.reader_token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ db, mfn }),
  }).catch(() => {});
}
function clearBookmarks(login, user) {
  const key = `irbis_bookmarks_${login}`;
  if (login === GUEST_LOGIN) { _saveGuestData(key, []); return; }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/bookmarks.php?action=clear`, user.reader_token, {
    method: 'POST',
  }).catch(() => {});
}
// «Мої запити» — читач (сесія 2026-08-12, міграція з localStorage у MySQL,
// queries.php). Гість, як і раніше, — sessionStorage (немає постійної особи).
// ⚠️ Локальний кеш (_storageFor, ключ irbis_queries_*) для читача НЕ прибрано —
// його паралельно продовжує писати saveQuery() в App.js, бо на ньому тримається
// синхронний дропдаун "історія" під полем пошуку (SearchQueryAutocomplete
// .refreshSuggestions читає сховище миттєво, без мережевого запиту при
// кожному натисканні клавіші). getQueries() тут — для вкладки «Мої запити»
// в кабінеті, MySQL лишається джерелом правди для неї.
async function getQueries(login, user = null) {
  if (login === GUEST_LOGIN) return _getGuestData(`irbis_queries_${GUEST_LOGIN}`);
  if (!user?.reader_token) return [];
  try {
    const r = await readerFetch(`${API_URL}/queries.php?action=list`, user.reader_token);
    const d = await r.json();
    return d.queries || [];
  } catch { return []; }
}
function removeQuery(login, user, queryObj) {
  if (login === GUEST_LOGIN) {
    const key = `irbis_queries_${GUEST_LOGIN}`;
    _saveGuestData(key, _getGuestData(key).filter(q => q !== queryObj));
    return;
  }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/queries.php?action=remove`, user.reader_token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: queryObj.query, author: queryObj.author, title: queryObj.title,
      discipline: queryObj.discipline, keywords: queryObj.keywords, db: queryObj.db,
      yearFrom: queryObj.yearFrom, searchType: queryObj.searchType,
    }),
  }).catch(() => {});
}
function clearQueries(login, user) {
  if (login === GUEST_LOGIN) { _saveGuestData(`irbis_queries_${GUEST_LOGIN}`, []); return; }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/queries.php?action=clear`, user.reader_token, { method: 'POST' }).catch(() => {});
}

// «Повні тексти» (перегляди в кабінеті) — читач (сесія 2026-08-12, міграція
// з localStorage у MySQL, fulltexts.php). На відміну від запитів вище —
// немає жодного синхронного споживача (немає власного автодоповнення), тому
// повністю на MySQL для читача, без паралельного локального кешу.
async function getFulltexts(login, user = null) {
  if (login === GUEST_LOGIN) return _getGuestData(`irbis_fulltexts_${GUEST_LOGIN}`);
  if (!user?.reader_token) return [];
  try {
    const r = await readerFetch(`${API_URL}/fulltexts.php?action=list`, user.reader_token);
    const d = await r.json();
    return d.fulltexts || [];
  } catch { return []; }
}
function saveFulltextEntry(login, entry, user = null) {
  if (login === GUEST_LOGIN) {
    const key = `irbis_fulltexts_${GUEST_LOGIN}`;
    const dedupeKey = e => `${e.mfn}|${e.db}|${e.url}`;
    const eKey = dedupeKey(entry);
    const existing = _getGuestData(key);
    _saveGuestData(key, [entry, ...existing.filter(e => dedupeKey(e) !== eKey)].slice(0, 100));
    return;
  }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/fulltexts.php?action=save`, user.reader_token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(entry),
  }).catch(() => {});
}
function removeFulltextEntry(login, user, entry) {
  if (login === GUEST_LOGIN) {
    const key = `irbis_fulltexts_${GUEST_LOGIN}`;
    const dedupeKey = e => `${e.mfn}|${e.db}|${e.url}`;
    _saveGuestData(key, _getGuestData(key).filter(e => dedupeKey(e) !== dedupeKey(entry)));
    return;
  }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/fulltexts.php?action=remove`, user.reader_token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ db: entry.db, mfn: entry.mfn, url: entry.url }),
  }).catch(() => {});
}
function removeFulltextGroup(login, user, db, mfn) {
  if (login === GUEST_LOGIN) {
    const key = `irbis_fulltexts_${GUEST_LOGIN}`;
    _saveGuestData(key, _getGuestData(key).filter(e => `${e.mfn}|${e.db}` !== `${mfn}|${db}`));
    return;
  }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/fulltexts.php?action=removeGroup`, user.reader_token, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ db, mfn }),
  }).catch(() => {});
}
function clearFulltexts(login, user) {
  if (login === GUEST_LOGIN) { _saveGuestData(`irbis_fulltexts_${GUEST_LOGIN}`, []); return; }
  if (!user?.reader_token) return;
  readerFetch(`${API_URL}/fulltexts.php?action=clear`, user.reader_token, { method: 'POST' }).catch(() => {});
}

// ─── Спільні запити «у польоті» (сесія 2026-10-25) ───────────────
// При вході в кабінет той самий важкий запит летів кілька разів
// одночасно: cabinet.php — фонова попередня загрузка в App.js + сам
// кабінет + бокова панель; order.php?action=list — лічильник вкладки +
// SectionOrders. На сервері кожен такий запит окремо читає ІРБІС (у
// cabinet.php холодна збірка активного читача — секунди). Тепер, якщо
// такий самий запит уже йде, наступний виклик просто чекає його відповіді.
// force-запит (кнопка «Оновити», автоопитувач) завжди йде новий і сам
// стає «у польоті» для наступних звичайних викликів.
const _inflight = {};
function _sharedReaderJson(key, url, token, force) {
  if (!force && _inflight[key]) return _inflight[key];
  const p = readerFetch(url, token)
    .then(r => r.json())
    .finally(() => { if (_inflight[key] === p) delete _inflight[key]; });
  _inflight[key] = p;
  return p;
}

// Дані кабінету (cabinet.php) + запис у sessionStorage-кеш кабінету.
function fetchCabinetData(user, force = false) {
  if (!user?.reader_token || !user?.login) return Promise.resolve({ error: 'no_token' });
  const cacheKey = `irbis_cabinet_${user.login}`;
  return _sharedReaderJson(
    `cab|${user.reader_token}`,
    `${API_URL}/cabinet.php${force ? '?force=1' : ''}`,
    user.reader_token, force,
  ).then(d => {
    if (!d.error) { try { sessionStorage.setItem(cacheKey, JSON.stringify(d)); } catch {} }
    return d;
  });
}

// Список замовлень читача (order.php?action=list).
function fetchOrderList(user, force = false) {
  if (!user?.reader_token) return Promise.resolve({ orders: [] });
  return _sharedReaderJson(
    `ord|${user.reader_token}`,
    `${API_URL}/order.php?action=list`,
    user.reader_token, force,
  );
}

// ─────────────────────────────────────────
// Конфиг навигационных секций
// ─────────────────────────────────────────
// СТАЛО — превратить NAV_ITEMS в функцию:
const getNavItems = (lang) => [
  { key: 'issued',    icon: 'fa-solid fa-book-open',         label: t(lang, 'cab_issued')    },
  { key: 'history',   icon: 'fa-solid fa-clock-rotate-left', label: t(lang, 'cab_history')   },
  { key: 'visits',    icon: 'fa-regular fa-calendar',        label: t(lang, 'cab_visits')    },
  { key: 'fulltexts', icon: 'fa-regular fa-file-lines',      label: t(lang, 'cab_fulltexts') },
  { key: 'bookmarks', icon: 'fa-solid fa-bookmark',          label: t(lang, 'cab_bookmarks') },
  { key: 'orders',    icon: 'fa-solid fa-cart-shopping',     label: t(lang, 'cab_orders')    },
  { key: 'queries',   icon: 'fa-solid fa-magnifying-glass',  label: t(lang, 'cab_queries')   },
];

// ─────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────
function EmptyState({ icon, text, hint }) {
  return (
    <div className="empty-state">
      <i className={`${icon} empty-state-icon`} />
      <div className="empty-state-text">{text}</div>
      {hint && <div className="empty-state-hint">{hint}</div>}
    </div>
  );
}

// ─────────────────────────────────────────
// Аватар читача
// ─────────────────────────────────────────
// Фото читача (поле 953 RDR) віддає reader_photo.php лише за X-Reader-Token
// (сесія 2026-10-25) — звичайний <img src> заголовок передати не може, тому
// беремо фото через readerFetch і показуємо як blob-URL. Один запит на токен
// за сесію сторінки: повторні монтування аватарки беруть уже готовий результат.
// Мережева помилка не запам'ятовується — наступне відкриття спробує знову.
const _readerPhotoCache = {};
function fetchReaderPhoto(user) {
  const token = user?.reader_token;
  if (!token) return Promise.resolve(null);
  if (!_readerPhotoCache[token]) {
    _readerPhotoCache[token] = readerFetch(`${API_URL}/reader_photo.php`, token)
      .then(r => (r.ok ? r.blob() : null))
      .then(b => (b && b.size > 0 && b.type.startsWith('image/') ? URL.createObjectURL(b) : null))
      .catch(() => { delete _readerPhotoCache[token]; return null; });
  }
  return _readerPhotoCache[token];
}

function ReaderAvatar({ data, user, size = 'md' }) {
  const hasPhoto = !!data?.has_photo;
  const [photoSrc, setPhotoSrc] = useState(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (!hasPhoto) { setPhotoSrc(null); return; }
    let alive = true;
    fetchReaderPhoto(user).then(src => { if (alive) { setImgError(false); setPhotoSrc(src); } });
    return () => { alive = false; };
  }, [hasPhoto, user]);

  const initials = (data?.name || user?.name || '?')
    .split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

  const cls = `sidebar-avatar sidebar-avatar--${size}`;

  // Поки фото вантажиться (або його немає) — одразу ініціали, без порожнього кола
  if (photoSrc && !imgError) {
    return (
      <div className={`${cls} sidebar-avatar--photo`}>
        <img src={photoSrc} alt={data?.name || user?.name || ''}
          className="sidebar-avatar-img"
          onError={() => setImgError(true)} />
      </div>
    );
  }
  return <div className={cls}>{initials}</div>;
}

// ─────────────────────────────────────────
// Спільні шматки рядка книги («Книги на руках» / «Історія видач»):
// чипи інв. № · шифр · місце та стовпчик дат. Стилі — ft-*/cab-* у MyCabinet.css
// ─────────────────────────────────────────
// hideInv — коли інв. № уже показано замість назви (запис без опису), щоб не дублювати
function CabBookMeta({ book, lang, hideInv = false }) {
  const showInv = !!book.inv_no && !hideInv;
  if (!showInv && !book.call_number && !book.department) return null;
  return (
    <div className="ft-meta">
      {showInv          && <span className="ft-meta-chip"><i className="fa-solid fa-barcode" />{t(lang, 'cab_inv_no')} {book.inv_no}</span>}
      {book.call_number && <span className="ft-meta-chip"><i className="fa-solid fa-tags" />{t(lang, 'cab_call_number')} {book.call_number}</span>}
      {book.department  && <span className="ft-meta-chip"><i className="fa-solid fa-location-dot" />{book.department}</span>}
    </div>
  );
}

function CabBookDates({ book, lang }) {
  return (
    <>
      {book.date_issued && (
        <span className="ft-file-date"><i className="fa-regular fa-calendar-plus" />{t(lang, 'cab_date_issued')} {book.date_issued}</span>
      )}
      {book.date_due && (
        <span className={`ft-file-date${book.is_overdue ? ' cab-date--overdue' : ''}`}>
          <i className="fa-regular fa-calendar-check" />{t(lang, 'cab_date_due')} {book.date_due}
        </span>
      )}
      {book.date_returned && (
        <span className="ft-file-date"><i className="fa-regular fa-calendar-xmark" />{t(lang, 'cab_date_returned')} {book.date_returned}</span>
      )}
    </>
  );
}

// ─────────────────────────────────────────
// BookRow — строка книги в формуляре
// ─────────────────────────────────────────
function BookRow({ book, index, onOpenBook, lang }) {
  const isOverdue  = book.is_overdue;
  const isReturned = !!book.date_returned;
  const canOpen    = !!(book.mfn && book.db && onOpenBook);

  const borderColor = isOverdue ? 'var(--color-danger)' : isReturned ? '#bdbdbd' : 'var(--color-success)';

  return (
    <ListGroup.Item className="px-4 py-3"
      style={{ borderLeft: `4px solid ${borderColor}`, background: isOverdue ? 'var(--color-danger-light)' : '#fff' }}>
      <Row className="align-items-start g-2">
        <Col xs={12} md={8}>
          <div className="d-flex gap-2 align-items-start">
            <span className="text-muted fw-bold flex-shrink-0 small" style={{ minWidth: '20px', paddingTop: '2px' }}>
              {index + 1}.
            </span>
            <div className="book-row-cover-wrap ft-cover-wrap" key={book.cover_url || 'no-cover'}>
              {book.cover_url && (
                <img src={book.cover_url} alt="" className="book-row-cover"
                  ref={coverImgRef} onError={coverImgError} />
              )}
              <div className="book-row-cover-placeholder cover-art-host" style={{ display: book.cover_url ? 'none' : 'flex' }}>
                <CoverArt db={book.db} />
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className={`history-book-title${canOpen ? ' history-book-title--link' : ''}`}
                onClick={() => canOpen && onOpenBook(book.mfn, book.db, book.description)}
                title={canOpen ? t(lang, 'cab_book_open_title') : ''}>
                {book.description || t(lang, 'cab_no_desc')}
              </div>
              <CabBookMeta book={book} lang={lang} />
            </div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="cab-side">
            {isReturned
              ? <span className="cab-status cab-status--returned"><i className="fa-solid fa-rotate-left" />{t(lang, 'cab_status_returned_f')}</span>
              : isOverdue
              ? <span className="cab-status cab-status--overdue"><i className="fa-solid fa-triangle-exclamation" />{t(lang, 'cab_status_overdue')}</span>
              : <span className="cab-status cab-status--on-hands"><i className="fa-solid fa-circle-check" />{t(lang, 'cab_status_on_hands')}</span>}
            <CabBookDates book={book} lang={lang} />
          </div>
        </Col>
      </Row>
    </ListGroup.Item>
  );
}

// ─────────────────────────────────────────
// SectionIssued
// ─────────────────────────────────────────
function SectionIssued({ books, onOpenBook, lang }) {
  if (books.length === 0)
    return <EmptyState icon="fa-solid fa-inbox" text={t(lang, 'cab_no_issued')} />;
  return scrollWrapIfLong(books,
    <ListGroup variant="flush">
      {books.map((book, i) => <BookRow key={i} book={book} index={i} onOpenBook={onOpenBook} lang={lang} />)}
    </ListGroup>
  );
}

// ─────────────────────────────────────────
// SectionHistory — аккордеон по статусам
// ─────────────────────────────────────────

const MONTH_NAMES = {
  uk: ['Січень','Лютий','Березень','Квітень','Травень','Червень',
       'Липень','Серпень','Вересень','Жовтень','Листопад','Грудень'],
  en: ['January','February','March','April','May','June',
       'July','August','September','October','November','December'],
};

function getReturnedMonthKey(d) {
  const p = (d || '').split('.');
  return p.length >= 3 ? `${p[1]}.${p[2]}` : '';
}

function formatMonthLabel(key, lang) {
  const [mm, yyyy] = key.split('.');
  const names = MONTH_NAMES[lang] || MONTH_NAMES.uk;
  return `${names[parseInt(mm, 10) - 1] || mm} ${yyyy}`;
}

function renderHistoryBook(book, bi, onOpenBook, lang) {
  const canOpen = !!(book.mfn && book.db && onOpenBook);
  return (
    <ListGroup.Item key={bi} className="history-book-item px-4 py-3">
      <Row className="align-items-start g-2">
        <Col xs={12} md={8}>
          <div className="d-flex gap-3 align-items-start">
            <div className="history-book-cover-wrap ft-cover-wrap" key={book.cover_url || 'no-cover'}>
              {book.cover_url && (
                <img src={book.cover_url} alt=""
                  className="history-book-cover"
                  ref={coverImgRef} onError={coverImgError} />
              )}
              <div className="history-book-cover-placeholder cover-art-host" style={{ display: book.cover_url ? 'none' : 'flex' }}>
                <CoverArt db={book.db} />
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className={`history-book-title${canOpen ? ' history-book-title--link' : ''}`}
                onClick={() => canOpen && onOpenBook(book.mfn, book.db, book.description)}
                title={canOpen ? t(lang, 'cab_book_open_title') : ''}>
                {book.description || (book.inv_no ? `${t(lang, 'cab_inv_no')} ${book.inv_no}` : t(lang, 'cab_no_desc'))}
              </div>
              <CabBookMeta book={book} lang={lang} hideInv={!book.description} />
            </div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="cab-side">
            <CabBookDates book={book} lang={lang} />
          </div>
        </Col>
      </Row>
    </ListGroup.Item>
  );
}

// ─────────────────────────────────────────
// HistSelect — кастомний дропдаун (десктоп) / нативний select (мобайл)
// ─────────────────────────────────────────
function HistSelect({ value, onChange, options, isYear }) {
  const [open, setOpen]       = useState(false);
  const [desktop, setDesktop] = useState(
    () => window.matchMedia('(min-width: 576px)').matches
  );
  const ref = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 576px)');
    const h = e => setDesktop(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const strVal  = String(value);
  const selOpt  = options.find(o => String(o.value) === strVal);
  const nativeCls = isYear ? 'hist-year-select' : 'hist-month-select';

  if (!desktop) {
    return (
      <select className={nativeCls} value={value}
        onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  return (
    <div ref={ref}
      className={`ctrl-dropdown hist-select ${isYear ? 'hist-select--year' : 'hist-select--month'}${open ? ' ctrl-dropdown--open' : ''}`}>
      <button type="button" className="ctrl-dropdown-trigger"
        onClick={() => setOpen(o => !o)}>
        <span className="ctrl-dropdown-label">{selOpt ? selOpt.label : value}</span>
        <i className="fa-solid fa-chevron-down ctrl-dropdown-chevron" />
      </button>
      {open && (
        <ul className="ctrl-dropdown-menu">
          {options.map(o => (
            <li key={o.value}
              className={`ctrl-dropdown-item${String(o.value) === strVal ? ' ctrl-dropdown-item--active' : ''}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              {o.label}
              {String(o.value) === strVal && (
                <i className="fa-solid fa-check ctrl-dropdown-item-check" />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReturnedWithMonthNav({ books, onOpenBook, lang }) {
  const { monthMap, years, yearMap } = useMemo(() => {
    const map = {};
    books.forEach(b => {
      const key = getReturnedMonthKey(b.date_returned);
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    const yMap = {};
    Object.keys(map).forEach(mk => {
      const yr = mk.split('.')[1];
      if (!yMap[yr]) yMap[yr] = [];
      yMap[yr].push(mk);
    });
    Object.keys(yMap).forEach(yr => {
      yMap[yr].sort((a, b) => parseInt(a) - parseInt(b));
    });
    const sortedYears = Object.keys(yMap).sort((a, b) => parseInt(b) - parseInt(a));
    return { monthMap: map, years: sortedYears, yearMap: yMap };
  }, [books]);

  const [selectedYear, setSelectedYear] = useState(() => years[0] || '');
  const [monthIdx, setMonthIdx] = useState(() => {
    const ms = yearMap[years[0]] || [];
    return Math.max(0, ms.length - 1);
  });

  useEffect(() => {
    setSelectedYear(prev => (years.length > 0 && !years.includes(prev)) ? years[0] : prev);
  }, [years]);

  useEffect(() => {
    const ms = yearMap[selectedYear] || [];
    setMonthIdx(Math.max(0, ms.length - 1));
  }, [selectedYear, yearMap]);

  const monthsForYear = yearMap[selectedYear] || [];
  const safeIdx = Math.min(Math.max(monthIdx, 0), monthsForYear.length - 1);
  const currentBooks = monthMap[monthsForYear[safeIdx]] || [];

  if (!years.length) return null;

  return (
    <>
      <div className="hist-period-nav">
        <HistSelect
          isYear
          value={selectedYear}
          onChange={setSelectedYear}
          options={years.map(yr => ({ value: yr, label: yr }))}
        />
        <button className="hist-month-btn"
          onClick={() => setMonthIdx(i => i - 1)}
          disabled={safeIdx <= 0}
          aria-label={t(lang, 'hist_prev_month')}>
          <i className="fa-solid fa-chevron-left" />
        </button>
        <HistSelect
          value={String(safeIdx)}
          onChange={v => setMonthIdx(Number(v))}
          options={monthsForYear.map((m, i) => ({
            value: String(i),
            label: `${formatMonthLabel(m, lang)} (${monthMap[m].length})`
          }))}
        />
        <button className="hist-month-btn"
          onClick={() => setMonthIdx(i => i + 1)}
          disabled={safeIdx >= monthsForYear.length - 1}
          aria-label={t(lang, 'hist_next_month')}>
          <i className="fa-solid fa-chevron-right" />
        </button>
      </div>
      <ListGroup variant="flush">
        {currentBooks.map((book, bi) => renderHistoryBook(book, bi, onOpenBook, lang))}
      </ListGroup>
    </>
  );
}

function SectionHistory({ books, onOpenBook, lang }) {
  if (books.length === 0)
    return <EmptyState icon="fa-solid fa-clock-rotate-left" text={t(lang, 'cab_no_history')} />;

  const ARCHIVE_CUTOFF = new Date().getFullYear() - 1;

  const getReturnedYear = b => {
    const p = (b.date_returned || '').split('.');
    return p.length >= 3 ? parseInt(p[2], 10) : 0;
  };

  const GROUPS = [
    { key: 'overdue',  icon: 'fa-solid fa-triangle-exclamation', label: t(lang, 'cab_status_overdue'),  modifier: 'overdue',  filter: b => b.is_overdue && !b.date_returned },
    { key: 'on_hands', icon: 'fa-solid fa-circle-check',         label: t(lang, 'cab_status_on_hands'), modifier: 'on-hands', filter: b => !b.is_overdue && !b.date_returned },
    { key: 'returned', icon: 'fa-solid fa-rotate-left',          label: t(lang, 'cab_returned'),        modifier: 'returned', filter: b => !!b.date_returned && getReturnedYear(b) >= ARCHIVE_CUTOFF },
  ];

  const groups = GROUPS.map(g => ({ ...g, books: books.filter(g.filter) })).filter(g => g.books.length > 0);

  const archiveReturned = books.filter(b => !!b.date_returned && getReturnedYear(b) < ARCHIVE_CUTOFF);

  return (
    <Accordion flush alwaysOpen defaultActiveKey={[]}>
      {groups.map((group, gi) => (
        <Accordion.Item key={group.key} eventKey={String(gi)}
          className={`history-accordion-item history-group--${group.modifier}`}>
          <Accordion.Header>
            <i className={`${group.icon} me-2`} />
            <span className="history-group-label">{group.label}</span>
            <span className="ft-head-count ms-2">{group.books.length}</span>
          </Accordion.Header>
          <Accordion.Body className="p-0">
            {group.key === 'returned'
              ? <ReturnedWithMonthNav books={group.books} onOpenBook={onOpenBook} lang={lang} />
              : <ListGroup variant="flush">
                  {group.books.map((book, bi) => renderHistoryBook(book, bi, onOpenBook, lang))}
                </ListGroup>
            }
          </Accordion.Body>
        </Accordion.Item>
      ))}
      {archiveReturned.length > 0 && (
        <Accordion.Item eventKey="archive" className="history-accordion-item history-group--archive">
          <Accordion.Header>
            <i className="fa-solid fa-box-archive me-2" />
            <span className="history-group-label">{t(lang, 'cab_archive_issued')}</span>
            <span className="ft-head-count ms-2">{archiveReturned.length}</span>
          </Accordion.Header>
          <Accordion.Body className="p-0">
            <ReturnedWithMonthNav books={archiveReturned} onOpenBook={onOpenBook} lang={lang} />
          </Accordion.Body>
        </Accordion.Item>
      )}
    </Accordion>
  );
}

// ─────────────────────────────────────────
// VisitsWithMonthNav — навігація по місяцях/роках
// ─────────────────────────────────────────
function VisitsWithMonthNav({ visits, lang }) {
  const { monthMap, yearMap, years } = useMemo(() => {
    const mm = {};
    visits.forEach(v => {
      const key = getReturnedMonthKey(v.date_issued);
      if (!mm[key]) mm[key] = [];
      mm[key].push(v);
    });
    const ym = {};
    Object.keys(mm).forEach(key => {
      const year = key.split('.')[1];
      if (!ym[year]) ym[year] = [];
      ym[year].push(key);
    });
    Object.values(ym).forEach(keys => keys.sort().reverse());
    const yrs = Object.keys(ym).sort().reverse();
    return { monthMap: mm, yearMap: ym, years: yrs };
  }, [visits]);

  const [selectedYear, setSelectedYear] = useState('');
  const [monthIdx, setMonthIdx]         = useState(0);

  useEffect(() => {
    if (years.length && !years.includes(selectedYear))
      setSelectedYear(years[0]);
  }, [years, selectedYear]);

  useEffect(() => { setMonthIdx(0); }, [selectedYear]);

  const monthKeys    = yearMap[selectedYear] || [];
  const currentKey   = monthKeys[monthIdx]   || '';
  const currentItems = monthMap[currentKey]  || [];

  return (
    <>
      <div className="hist-period-nav">
        <HistSelect
          isYear
          value={selectedYear}
          onChange={setSelectedYear}
          options={years.map(y => ({ value: y, label: y }))}
        />
        <button className="hist-month-btn" disabled={monthIdx === 0}
          onClick={() => setMonthIdx(i => i - 1)}>
          <i className="fa-solid fa-chevron-left" />
        </button>
        <HistSelect
          value={String(monthIdx)}
          onChange={v => setMonthIdx(Number(v))}
          options={monthKeys.map((k, i) => ({
            value: String(i),
            label: formatMonthLabel(k, lang)
          }))}
        />
        <button className="hist-month-btn" disabled={monthIdx === monthKeys.length - 1}
          onClick={() => setMonthIdx(i => i + 1)}>
          <i className="fa-solid fa-chevron-right" />
        </button>
      </div>
      <div className="visits-month-list">
        {currentItems.map((v, i) => (
          <div key={i} className="visits-group-row">
            <span className="visits-group-row-num">{i + 1}.</span>
            <span className="visits-group-row-date">
              {v.date_issued
                ? <><i className="fa-regular fa-calendar-days me-1" />{v.date_issued}</>
                : <span className="text-muted">—</span>}
            </span>
            {v.description && (
              <span className="visits-group-row-desc">
                <i className="fa-solid fa-location-dot me-1" />{v.description.replace(/\s*\([^)]*\)\s*$/, '')}
              </span>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

// ─────────────────────────────────────────
// SectionVisits — акордеон по місцях
// ─────────────────────────────────────────
function SectionVisits({ visits, lang }) {
  if (visits.length === 0)
    return <EmptyState icon="fa-regular fa-calendar" text={t(lang, 'cab_no_visits')} />;

  const ARCHIVE_CUTOFF = new Date().getFullYear() - 1;

  const getVisitYear = v => {
    const p = (v.date_issued || '').split('.');
    return p.length >= 3 ? parseInt(p[2], 10) : 0;
  };

  const recentVisits  = visits.filter(v => getVisitYear(v) >= ARCHIVE_CUTOFF);
  const archiveVisits = visits.filter(v => getVisitYear(v) < ARCHIVE_CUTOFF);

  return (
    <div className="visits-groups">
      <div className="visits-head">
        <span className="ft-head-title">
          {t(lang, 'cab_vs_heading')}
          <span className="ft-head-count">{visits.length}</span>
        </span>
      </div>
      <VisitsWithMonthNav visits={recentVisits} lang={lang} />
      {archiveVisits.length > 0 && (
        <Accordion flush className="mt-3">
          <Accordion.Item eventKey="0" className="history-accordion-item history-group--archive">
            <Accordion.Header>
              <i className="fa-solid fa-box-archive me-2" />
              <span className="history-group-label">{t(lang, 'cab_archive_visits')}</span>
              <span className="ft-head-count ms-2">{archiveVisits.length}</span>
            </Accordion.Header>
            <Accordion.Body className="p-0">
              <VisitsWithMonthNav visits={archiveVisits} lang={lang} />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// SectionBookmarks
// ─────────────────────────────────────────
function SectionBookmarks({ login, onOpenBook, user, lang, trackEvent, onOrderPlaced, onNavigateSection }) {
  const [bookmarks, setBookmarks] = useState([]);
  // Швидке замовлення прямо з картки закладки (сесія 2026-08-22) — модалка
  // вибору пункту видачі (order.php) відкривається напряму, без переходу на
  // повну картку видання. Гостю (без reader_token) замовлення недоступне —
  // для нього кнопка, як і раніше, веде на картку (там — плашка входу).
  const [quickOrderTarget, setQuickOrderTarget] = useState(null); // { db, mfn } | null

  useEffect(() => {
    let alive = true;
    getBookmarks(login, user).then(bm => { if (alive) setBookmarks(bm); });
    return () => { alive = false; };
  }, [login, user]);

  const handleRemoveBookmark = (db, mfn) => {
    setBookmarks(prev => prev.filter(b => !(b.mfn === mfn && b.db === db)));
    removeBookmark(login, user, db, mfn);
    if (login === GUEST_LOGIN && trackEvent) trackEvent('bookmark', { detail: 'remove', db_name: db, mfn });
  };

  const clearAll = () => {
    setBookmarks([]);
    clearBookmarks(login, user);
    if (login === GUEST_LOGIN && trackEvent) trackEvent('bookmark', { detail: 'clear' });
  };

  if (bookmarks.length === 0)
    return <EmptyState icon="fa-regular fa-bookmark" text={t(lang, 'cab_no_bookmarks')}
      hint={t(lang, 'cab_no_bookmarks_hint')} />;

  return (
    <>
      <div className="ft-head">
        <span className="ft-head-title">
          {t(lang, 'cab_bm_heading')}
          <span className="ft-head-count">{bookmarks.length}</span>
        </span>
        <button type="button" className="ft-clear-btn" onClick={clearAll}>
          <i className="fa-solid fa-trash" />{t(lang, 'cab_bookmarks_clear_all')}
        </button>
      </div>
      {scrollWrapIfLong(bookmarks,
      <ListGroup variant="flush">
        {bookmarks.map((b, i) => (
        <ListGroup.Item key={i} className="px-4 py-3">
          <div className="d-flex align-items-start gap-3">
            <div className="bookmark-cover-wrap ft-cover-wrap flex-shrink-0" key={b.cover_url || 'no-cover'}
              onClick={() => onOpenBook(b.mfn, b.db, b.title)}
              style={{ cursor: 'pointer' }}>
              {b.cover_url ? (
                <img src={b.cover_url} alt=""
                  className="bookmark-cover"
                  ref={coverImgRef} onError={coverImgError} />
              ) : null}
              <div className="bookmark-cover-placeholder cover-art-host"
                style={{ display: b.cover_url ? 'none' : 'flex' }}>
                <CoverArt db={b.db} />
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="history-book-title history-book-title--link"
                onClick={() => onOpenBook(b.mfn, b.db, b.title)}
                title={t(lang, 'cab_book_open_title')}>
                {b.title}
              </div>
              <div className="ft-meta">
                {b.author && (
                  <span className="ft-meta-item"><i className="fa-solid fa-user" />{b.author}</span>
                )}
                {b.db && (
                  <span className="ft-meta-chip"><i className="fa-solid fa-database" />{b.db}</span>
                )}
                {b.year && (
                  <span className="ft-meta-chip"><i className="fa-regular fa-calendar" />{b.year}</span>
                )}
              </div>
              {b.addedAt && (
                <div className="ft-file-date bm-added">
                  <i className="fa-regular fa-clock" />{t(lang, 'cab_bookmark_added')} {b.addedAt}
                </div>
              )}
            </div>
            <div className="bm-actions">
              <button type="button" className="ft-icon-btn"
                onClick={() => handleRemoveBookmark(b.db, b.mfn)}
                title={t(lang, 'cab_bookmark_remove')}>
                <i className="fa-solid fa-trash" />
              </button>
              <button type="button" className="ft-open-btn"
                onClick={() => login === GUEST_LOGIN
                  ? onOpenBook(b.mfn, b.db, b.title)
                  : setQuickOrderTarget({ db: b.db, mfn: b.mfn })}>
                <i className="fa-solid fa-cart-shopping" />{t(lang, 'book_order')}
              </button>
            </div>
          </div>
        </ListGroup.Item>
      ))}
    </ListGroup>
      )}
      {quickOrderTarget && (
        <OrderModal
          db={quickOrderTarget.db}
          mfn={quickOrderTarget.mfn}
          user={user}
          lang={lang}
          trackEvent={trackEvent}
          onOrderPlaced={onOrderPlaced}
          onOpenCabinetSection={onNavigateSection}
          onClose={() => setQuickOrderTarget(null)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────
// SectionQueries
// ─────────────────────────────────────────
// keywords («Шукати у знайденому») — у кінці і лише коли непорожнє: той самий
// формат, що queriesDedupeKey() у queries.php (ключі старих запитів не змінюються)
const _qDedupeKey = q => `${q.query||''}|${q.author||''}|${q.title||''}|${q.discipline||''}|${q.db||''}|${q.yearFrom||''}|${q.searchType||''}`
  + (q.keywords ? `|${q.keywords}` : '');

function SectionQueries({ login, onRepeatSearch, user, lang, trackEvent }) {
  const [queries, setQueries] = useState([]);
  useEffect(() => {
    let alive = true;
    getQueries(login, user).then(raw => {
      if (!alive) return;
      const seen = new Set();
      const deduped = raw.filter(q => {
        const k = _qDedupeKey(q);
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
      setQueries(deduped);
    });
    return () => { alive = false; };
  }, [login, user]);

  const handleRemoveQuery = queryRef => {
    setQueries(prev => prev.filter(q => q !== queryRef));
    removeQuery(login, user, queryRef);
    if (login === GUEST_LOGIN && trackEvent) trackEvent('query', { detail: 'remove' });
  };

  const clearAll = () => {
    setQueries([]);
    clearQueries(login, user);
    if (login === GUEST_LOGIN && trackEvent) trackEvent('query', { detail: 'clear' });
  };

  const sortedQueries = useMemo(() =>
    [...queries].sort((a, b) => {
      const kA = (a.query || a.author || a.title || a.discipline || '').toLowerCase();
      const kB = (b.query || b.author || b.title || b.discipline || '').toLowerCase();
      return kA.localeCompare(kB, 'uk');
    }),
  [queries]);

  if (queries.length === 0)
    return <EmptyState icon="fa-solid fa-magnifying-glass" text={t(lang, 'cab_no_queries')}
      hint={t(lang, 'cab_no_queries_hint')} />;

  return (
    <>
      <div className="ft-head">
        <span className="ft-head-title">
          {t(lang, 'cab_qs_heading')}
          <span className="ft-head-count">{queries.length}</span>
        </span>
        <button type="button" className="ft-clear-btn" onClick={clearAll}>
          <i className="fa-solid fa-trash" />{t(lang, 'cab_queries_clear_all')}
        </button>
      </div>
      <div className="queries-scroll-wrap">
        <ListGroup variant="flush">
          {sortedQueries.map((q, i) => {
            const parts = [
              q.query      && `${t(lang, 'cab_q_kw')}: «${q.query}»`,
              q.author     && `${t(lang, 'cab_q_author')}: «${q.author}»`,
              q.title      && `${t(lang, 'cab_q_title')}: «${q.title}»`,
              q.discipline && `${t(lang, 'cab_q_discipline')}: «${q.discipline}»`,
              q.keywords   && `${t(lang, 'cab_q_narrow')}: «${q.keywords}»`,
              q.yearFrom   && `${t(lang, 'cab_q_year')}: ${q.yearFrom}`,
            ].filter(Boolean);
            return (
              <ListGroup.Item key={i} className="px-4 py-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="query-text" onClick={() => onRepeatSearch(q)}>
                      <i className="fa-solid fa-magnifying-glass me-2" />{parts.join(' · ')}
                    </div>
                    {q.db && (
                      <div className="ft-meta">
                        <span className="ft-meta-chip"><i className="fa-solid fa-database" />{q.db}</span>
                      </div>
                    )}
                  </div>
                  <div className="ft-file-actions">
                    <button type="button" className="ft-open-btn"
                      onClick={() => onRepeatSearch(q)} title={t(lang, 'cab_query_repeat_title')}>
                      <i className="fa-solid fa-play d-none d-sm-inline" />{t(lang, 'cab_query_repeat')}
                    </button>
                    <button type="button" className="ft-icon-btn"
                      onClick={() => handleRemoveQuery(q)} title={t(lang, 'cab_query_remove')}>
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                </div>
              </ListGroup.Item>
            );
          })}
        </ListGroup>
      </div>
    </>
  );
}

// ─────────────────────────────────────────
// SectionOrders — замовлення, надіслані до RQST (order.php)
// ─────────────────────────────────────────
// Колір статусу — у CSS (.cab-status--{status}, MyCabinet.css), тут лише іконка
const ORDER_STATUS_STYLE = {
  pending:   { icon: 'fa-solid fa-hourglass-half' },
  reserved:  { icon: 'fa-solid fa-box-archive' },
  issued:    { icon: 'fa-solid fa-circle-check' },
  cancelled: { icon: 'fa-solid fa-rotate-left' },
  declined:  { icon: 'fa-solid fa-ban' },
};

const DECLINE_REASON_CODES = ['01', '02', '03', '04', '05', '06', '07', '08', '09'];

// «Неактивні» — поріг «недавній»/«Архів» (сесія 2026-08-12): без цього список
// закритих замовлень активного читача розростався б безлімітно. Недавні
// (< 30 днів) показуються одразу в «Неактивні», старіші — у вкладеному
// акордеоні «Архів» зі своєю пагінацією (той самий PaginationBlock, що й у
// JournalPage.js).
const ORDER_ARCHIVE_CUTOFF_DAYS = 30;
const ORDER_ARCHIVE_PER_PAGE    = 10;

// Внутрішній скрол для «Активні»/недавньої частини «Неактивні» — той самий
// спільний поріг+хелпер (CABINET_LIST_SCROLL_THRESHOLD/scrollWrapIfLong),
// що й «Книги на руках»/«Закладки»/«Повні тексти» (див. початок файлу).
function renderOrderList(list, { onOpenBook, lang }) {
  return scrollWrapIfLong(list,
    <ListGroup variant="flush">
      {list.map(o => renderOrderRow(o, { onOpenBook, lang }))}
    </ListGroup>
  );
}

// Рядок одного замовлення — винесено з SectionOrders (сесія 2026-08-12,
// групування «Активні»/«Неактивні»), щоб той самий рендер перевикористовувався
// і в несворачуваному блоці «Активні», і в акордеоні «Неактивні». Кнопку
// видалення прибрано разом з механізмом hidden_orders — акордеон і так
// ховає закриті заявки, ручне приховування втратило сенс.
function renderOrderRow(o, { onOpenBook, lang }) {
  const status  = ORDER_STATUS_STYLE[o.status] ? o.status : 'pending';
  const st      = ORDER_STATUS_STYLE[status];
  const canOpen = !!(o.mfn && o.db && onOpenBook);
  return (
    <ListGroup.Item key={o.id} className="px-4 py-3">
      <Row className="align-items-start g-2">
        <Col xs={12} md={8}>
          <div className="d-flex gap-3 align-items-start">
            <div className="book-row-cover-wrap ft-cover-wrap">
              {o.cover_url && (
                <img src={o.cover_url} alt="" className="book-row-cover"
                  ref={coverImgRef} onError={coverImgError} />
              )}
              <div className="book-row-cover-placeholder cover-art-host" style={{ display: o.cover_url ? 'none' : 'flex' }}>
                <CoverArt db={o.db} />
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className={`history-book-title${canOpen ? ' history-book-title--link' : ''}`}
                onClick={() => canOpen && onOpenBook(o.mfn, o.db, o.citation)}
                title={canOpen ? t(lang, 'cab_book_open_title') : ''}>
                {o.citation}
              </div>
              <div className="ft-meta">
                {o.pickup_label && (
                  <span className="ft-meta-chip order-pickup-chip">
                    <i className="fa-solid fa-location-dot" />{t(lang, 'cab_order_pickup')} {o.pickup_label}
                  </span>
                )}
                {o.shifr && (
                  <span className="ft-meta-chip">
                    <i className="fa-solid fa-tags" />{t(lang, 'cab_call_number')} {o.shifr}
                  </span>
                )}
              </div>
              {o.status === 'declined' && (
                <div className="order-decline-reason">
                  <i className="fa-solid fa-circle-info" />
                  {t(lang, 'cab_order_decline_reason')} {t(lang, DECLINE_REASON_CODES.includes(o.decline_reason_code) ? `cab_order_decline_reason_${o.decline_reason_code}` : 'cab_order_decline_reason_other')}
                </div>
              )}
            </div>
          </div>
        </Col>
        <Col xs={12} md={4}>
          <div className="cab-side">
            <span className={`cab-status cab-status--${status}`}>
              <i className={st.icon} />{t(lang, `cab_order_status_${o.status}`)}
            </span>
            {o.ordered_at && (
              <span className="ft-file-date">
                <i className="fa-regular fa-calendar-plus" />{t(lang, 'cab_order_date')} {o.ordered_at}
              </span>
            )}
          </div>
        </Col>
      </Row>
    </ListGroup.Item>
  );
}

function SectionOrders({ login, user, onOpenBook, lang, refreshTrigger }) {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [archivePage, setArchivePage] = useState(1);

  useEffect(() => {
    let alive = true;
    if (!user?.reader_token) { setOrders([]); setLoading(false); return; }
    setLoading(true); setError('');
    fetchOrderList(user)
      .then(d => { if (alive) setOrders(d.orders || []); })
      .catch(() => { if (alive) setError(t(lang, 'cab_conn_error')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [login, user, lang, refreshTrigger]);

  useEffect(() => { setArchivePage(1); }, [orders]);

  if (loading) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger" className="d-flex align-items-center gap-2">
          <i className="fa-solid fa-triangle-exclamation flex-shrink-0" />{error}
        </Alert>
      </div>
    );
  }

  if (orders.length === 0)
    return <EmptyState icon="fa-solid fa-cart-shopping" text={t(lang, 'cab_no_orders')}
      hint={t(lang, 'cab_no_orders_hint')} />;

  // «Активні» (pending+reserved) — несворачуваний блок, читач не може
  // випадково приховати заброньоване/необроблене клацанням (навмисна
  // відмінність від SectionHistory, де навіть «Прострочено»/«На руках» —
  // звичайні згортувані Accordion.Item; тут же йдеться про потенційно
  // важливі до дії заявки, тож ховати їх кліком небажано). issued сюди
  // не потрапляє — вже відфільтровано на бекенді (order.php, action=list).
  const activeOrders = orders.filter(o => o.status === 'pending' || o.status === 'reserved');
  const closedOrders = orders.filter(o => o.status === 'cancelled' || o.status === 'declined');

  // «Неактивні»/«Архів» — сиблінги в ОДНОМУ Accordion (alwaysOpen), а не
  // вкладені один в одного — за точним зразком SectionHistory («Повернено»
  // показує лише недавнє, «Архів» — окремий елемент того ж рівня для
  // старішого). «Неактивні» = недавні (< ORDER_ARCHIVE_CUTOFF_DAYS), «Архів»
  // = старіші, зі своєю пагінацією (сесія 2026-08-12) — без цього список
  // активного читача розростався б безлімітно.
  const archiveCutoffTs = Date.now() / 1000 - ORDER_ARCHIVE_CUTOFF_DAYS * 86400;
  const recentClosed   = closedOrders.filter(o => (o.ordered_at_ts || 0) >= archiveCutoffTs);
  const archivedClosed = closedOrders.filter(o => (o.ordered_at_ts || 0) < archiveCutoffTs);

  const archiveTotalPages = Math.max(1, Math.ceil(archivedClosed.length / ORDER_ARCHIVE_PER_PAGE));
  const archivePageSafe   = Math.min(archivePage, archiveTotalPages);
  const archivePageItems  = archivedClosed.slice(
    (archivePageSafe - 1) * ORDER_ARCHIVE_PER_PAGE,
    archivePageSafe * ORDER_ARCHIVE_PER_PAGE
  );

  return (
    <>
      {activeOrders.length > 0 && (
        <>
          <div className="order-active-header">
            <i className="fa-solid fa-cart-shopping me-2" />
            <span className="order-group-label">{t(lang, 'cab_orders_active')}</span>
            <span className="ft-head-count ms-2">{activeOrders.length}</span>
          </div>
          {renderOrderList(activeOrders, { onOpenBook, lang })}
        </>
      )}
      {closedOrders.length > 0 && (
        <Accordion flush alwaysOpen defaultActiveKey={[]} className={activeOrders.length > 0 ? 'mt-3' : ''}>
          {recentClosed.length > 0 && (
            <Accordion.Item eventKey="closed" className="order-accordion-item order-group--closed">
              <Accordion.Header>
                <i className="fa-solid fa-box-archive me-2" />
                <span className="order-group-label">{t(lang, 'cab_orders_inactive')}</span>
                <span className="ft-head-count ms-2">{recentClosed.length}</span>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                {renderOrderList(recentClosed, { onOpenBook, lang })}
              </Accordion.Body>
            </Accordion.Item>
          )}
          {archivedClosed.length > 0 && (
            <Accordion.Item eventKey="archive" className="order-accordion-item order-group--archive">
              <Accordion.Header>
                <i className="fa-solid fa-clock-rotate-left me-2" />
                <span className="order-group-label">{t(lang, 'cab_archive_orders')}</span>
                <span className="ft-head-count ms-2">{archivedClosed.length}</span>
              </Accordion.Header>
              <Accordion.Body className="p-0">
                <ListGroup variant="flush">
                  {archivePageItems.map(o => renderOrderRow(o, { onOpenBook, lang }))}
                </ListGroup>
                {archiveTotalPages > 1 && (
                  <PaginationBlock page={archivePageSafe} totalPages={archiveTotalPages}
                    onGo={setArchivePage} className="order-archive-pagination" lang={lang} />
                )}
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>
      )}
    </>
  );
}

function SectionFulltexts({ login, onOpenBook, user, lang }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    let alive = true;
    getFulltexts(login, user).then(fs => { if (alive) setEntries(fs); });
    return () => { alive = false; };
  }, [login, user]);

  const removeEntry = ref => {
    setEntries(prev => prev.filter(e => e !== ref));
    removeFulltextEntry(login, user, ref);
  };
  const removeGroup = groupKey => {
    setEntries(prev => prev.filter(e => `${e.mfn}|${e.db}` !== groupKey));
    const [mfnStr, groupDb] = groupKey.split('|');
    removeFulltextGroup(login, user, groupDb, Number(mfnStr));
  };
  const clearAll = () => {
    setEntries([]);
    clearFulltexts(login, user);
  };

  const groups = useMemo(() => {
    const map = {}, order = [];
    entries.forEach(e => {
      const gKey = `${e.mfn}|${e.db}`;
      if (!map[gKey]) {
        map[gKey] = { key: gKey, mfn: e.mfn, db: e.db, title: e.title, author: e.author, year: e.year, files: [] };
        order.push(gKey);
      }
      map[gKey].files.push(e);
    });
    return order.map(k => map[k]);
  }, [entries]);

  if (entries.length === 0)
    return <EmptyState icon="fa-regular fa-file-lines"
      text={t(lang, 'cab_no_fulltexts')} hint={t(lang, 'cab_fulltexts_hint')} />;

  return (
    <>
      <div className="ft-head">
        <span className="ft-head-title">
          {t(lang, 'cab_ft_heading')}
          <span className="ft-head-count">{groups.length}</span>
        </span>
        <button type="button" className="ft-clear-btn" onClick={clearAll}>
          <i className="fa-solid fa-trash" />{t(lang, 'cab_fulltexts_clear_all')}
        </button>
      </div>
      {scrollWrapIfLong(groups,
      <ListGroup variant="flush">
        {groups.map(group => (
          <ListGroup.Item key={group.key} className="px-4 py-3 ft-group">
            <div className="d-flex align-items-start gap-3">
              <div className="bookmark-cover-wrap ft-cover-wrap flex-shrink-0">
                <img
                  src={`${API_URL}/cover.php?db=${group.db}&mfn=${group.mfn}`}
                  alt=""
                  className="bookmark-cover"
                  ref={coverImgRef} onError={coverImgError}
                />
                <div className="bookmark-cover-placeholder cover-art-host" style={{ display: 'none' }}>
                  <CoverArt db={group.db} />
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
            <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
              <div style={{ minWidth: 0 }}>
                <div
                  className="history-book-title history-book-title--link"
                  onClick={() => group.mfn && onOpenBook && onOpenBook(group.mfn, group.db, group.title)}
                  title={t(lang, 'cab_book_open_title')}
                >
                  {group.title || `MFN ${group.mfn}`}
                </div>
                <div className="ft-meta">
                  {group.author && (
                    <span className="ft-meta-item">
                      <i className="fa-solid fa-user" />{group.author}
                    </span>
                  )}
                  {group.db && (
                    <span className="ft-meta-chip">
                      <i className="fa-solid fa-database" />{group.db}
                    </span>
                  )}
                  {group.year && (
                    <span className="ft-meta-chip">
                      <i className="fa-regular fa-calendar" />{group.year}
                    </span>
                  )}
                </div>
              </div>
              <button type="button" className="ft-icon-btn ft-icon-btn--group"
                onClick={() => removeGroup(group.key)}
                title={t(lang, 'cab_ft_remove_group')}>
                <i className="fa-solid fa-trash" />
              </button>
            </div>
            <div className="ft-files-list">
              {group.files.map((e, fi) => (
                <div key={fi} className="ft-file-row">
                  <div className="ft-file-info">
                    {e.source && (
                      <span className={`ft-file-type ft-file-type--${e.source === 'internal' ? 'internal' : 'external'}`}>
                        <i className={`fa-solid ${e.source === 'internal' ? 'fa-file-pdf' : 'fa-arrow-up-right-from-square'}`} />
                        {e.label || t(lang, e.source === 'internal' ? 'ft_int_label' : 'ft_ext_label')}
                      </span>
                    )}
                    {e.accessedAt && (
                      <span className="ft-file-date">
                        <i className="fa-regular fa-clock" />{t(lang, 'cab_ft_viewed')} {e.accessedAt}
                      </span>
                    )}
                  </div>
                  <div className="ft-file-actions">
                    {/* Тимчасово вимкнено (2026-11-03): внутрішні повні тексти не відкриваються,
                        як і кнопка в BookFullView.js. Щоб увімкнути — залишити тільки <a> */}
                    {e.source === 'internal' ? (
                      <span className="ft-open-btn ft-open-btn--disabled" aria-disabled="true">
                        <i className="fa-solid fa-book-open-reader" />{t(lang, 'cab_ft_open')}
                      </span>
                    ) : (
                      <a href={_safeExtUrl(e.url)} target="_blank" rel="noreferrer" className="ft-open-btn">
                        <i className="fa-solid fa-book-open-reader" />{t(lang, 'cab_ft_open')}
                      </a>
                    )}
                    <button type="button" className="ft-icon-btn"
                      onClick={() => removeEntry(e)} title={t(lang, 'cab_ft_remove')}>
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
              </div>
            </div>
          </ListGroup.Item>
        ))}
      </ListGroup>
      )}
    </>
  );
}


// ─────────────────────────────────────────
// CabinetPage
// ─────────────────────────────────────────
function CabinetPage({ user, initialSection, onOpenBook, onRepeatSearch, onSectionChange, backLabel, onBack, lang = 'uk', trackEvent, onOrderPlaced }) {
  const isGuest = user?.mode === 'guest';
  const cacheKey = !isGuest && user?.login
  ? `irbis_cabinet_${user.login}`
  : null;
  

const allNav = getNavItems(lang);
const GUEST_NAV = allNav.filter(item =>
  item.key === 'bookmarks' || item.key === 'queries'
);
const activeNavItems = isGuest ? GUEST_NAV : allNav;

  const safeInitial = initialSection
    ? (isGuest && !['bookmarks', 'queries'].includes(initialSection) ? 'bookmarks' : initialSection)
    : (isGuest ? 'bookmarks' : 'issued');

    const [data, setData] = useState(() => {
      if (isGuest || !cacheKey) return null;
      try {
        const s = sessionStorage.getItem(cacheKey);
        return s ? JSON.parse(s) : null;
      } catch { return null; }
    });
    const [loading, setLoading] = useState(() => {
      if (isGuest) return false;
      if (cacheKey) {
        try { return sessionStorage.getItem(cacheKey) === null; } catch {}
      }
      return true;
    });
  const [error, setError]           = useState('');
  const [activeSection, setSection] = useState(safeInitial);

  useEffect(() => {
    if (!initialSection) return;
    const safe = isGuest && !['bookmarks', 'queries'].includes(initialSection)
      ? 'bookmarks'
      : initialSection;
    setSection(safe);
  }, [initialSection, isGuest]);

  // force=true (кнопка «Оновити») — обходить і клієнтський sessionStorage,
  // і серверний файловий кеш cabinet.php (?force=1, сесія 2026-08-12).
  // Знайдено живим тестом: без цього щойно видана бібліотекарем книга не
  // з'являлась у "Книги на руках" одразу — сервер однаково віддавав до
  // 5 хв застарілий кеш, кнопка "Оновити" раніше чистила лише sessionStorage.
  const loadData = useCallback((force = false) => {
    if (isGuest) { setLoading(false); return; }
    if (!user?.login) {
      setError(t(lang, 'cab_guest_only'));
      setLoading(false); return;
    }
    // Перевіряємо кеш сесії — якщо є, показуємо миттєво
    if (!force && cacheKey) {
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) {
          // data вже могла бути ініціалізована через lazy init —
          // не перемальовуємо зайвий раз якщо вже є дані
          setData(prev => prev ?? JSON.parse(cached));
          setLoading(false);
          return;
        }
      } catch {}
    }
    setLoading(true); setError('');
    // Якщо App.js вже запустив фонову попередню загрузку — чекаємо її,
    // а не запускаємо другу збірку кабінету на сервері.
    fetchCabinetData(user, force)
      .then(d => {
        if (d.error) setError(d.error);
        else setData(d);
      })
      .catch(() => setError(t(lang, 'cab_conn_error')))
      .finally(() => setLoading(false));
  }, [user, isGuest, cacheKey, lang]);
  const [ordersRefreshTick, setOrdersRefreshTick] = useState(0);
  const refreshData = useCallback(() => {
    if (cacheKey) {
      try { sessionStorage.removeItem(cacheKey); } catch {}
    }
    loadData(true);
    setOrdersRefreshTick(t => t + 1);
  }, [cacheKey, loadData]);

  useEffect(() => { loadData(); }, [loadData]);

  // Тихий автоопитувач «Книги на руках» (сесія 2026-08-12) — реального
  // push від ІРБІС сервер не дає (АРМ ніяк не сповіщає веб про свої дії),
  // тож "динаміка" — це періодичний force-запит по таймеру, той самий
  // ?force=1, що й кнопка "Оновити". Працює ЛИШЕ поки відкрито саме цей
  // розділ і вкладка в фокусі (document.hidden) — щоб не множити зайві
  // TCP-з'єднання до ІРБІС на кожен відкритий кабінет. Не займає стан
  // loading/error — оновлює data мовчки, без миготіння спінером; помилку
  // просто пропускає (кнопка "Оновити" лишається підстраховкою вручну).
  useEffect(() => {
    if (isGuest || activeSection !== 'issued' || !user?.reader_token) return;
    const POLL_MS = 90000;
    let cancelled = false;

    const id = setInterval(() => {
      if (document.hidden) return;
      fetchCabinetData(user, true)
        .then(d => {
          if (cancelled || d.error) return;
          setData(d);
        })
        .catch(() => {});
    }, POLL_MS);

    return () => { cancelled = true; clearInterval(id); };
  }, [isGuest, activeSection, user, cacheKey]);

  const handleSectionChange = key => {
    setSection(key);
    onSectionChange?.(key);
  };

  const effectiveLogin = user?.login || GUEST_LOGIN;
  const overdueCount   = data ? data.issued.filter(b => b.is_overdue).length : 0;
  const [bookmarksCount, setBookmarksCount] = useState(0);
  useEffect(() => {
    let alive = true;
    getBookmarks(effectiveLogin, user).then(bm => { if (alive) setBookmarksCount(bm.length); });
    return () => { alive = false; };
  }, [effectiveLogin, user]);
  const [queriesCount, setQueriesCount] = useState(0);
  useEffect(() => {
    let alive = true;
    getQueries(effectiveLogin, user).then(qs => { if (alive) setQueriesCount(qs.length); });
    return () => { alive = false; };
  }, [effectiveLogin, user]);
  const [fulltextsCount, setFulltextsCount] = useState(0);
  useEffect(() => {
    let alive = true;
    getFulltexts(effectiveLogin, user).then(fs => {
      if (alive) setFulltextsCount(new Set(fs.map(e => `${e.mfn}|${e.db}`)).size);
    });
    return () => { alive = false; };
  }, [effectiveLogin, user]);

  const [ordersCount, setOrdersCount] = useState(0);
  useEffect(() => {
    let alive = true;
    if (isGuest || !user?.reader_token) { setOrdersCount(0); return; }
    fetchOrderList(user)
      .then(d => { if (alive) setOrdersCount((d.orders || []).length); })
      .catch(() => {});
    return () => { alive = false; };
  }, [isGuest, user, ordersRefreshTick]);

  const badges = {
    issued:     data ? data.issued.length  : null,
    history:    data ? data.history.length : null,
    visits:     data ? data.visits.length  : null,
    fulltexts:  fulltextsCount || null,
    bookmarks:  bookmarksCount || null,
    orders:     ordersCount    || null,
    queries:    queriesCount   || null,
  };

  const currentNavItem = activeNavItems.find(i => i.key === activeSection) || activeNavItems[0];

  return (
    <div className="cabinet-page">

      {/* ── Шапка секції ── */}
      <div className="cabinet-page-header">
        <div className="cabinet-page-header-inner">

          {/* Профіль */}
          <div className="cabinet-page-profile">
            {isGuest ? (
              <div className="sidebar-avatar" style={{ background: 'linear-gradient(135deg, #4a6576, #6a8a9a)' }}>
                <i className="fa-solid fa-user-secret" />
              </div>
            ) : (
              <ReaderAvatar data={data} user={user} size="md" />
            )}
            <div>
              <div className="cabinet-page-username">
                {isGuest ? t(lang, 'cab_guest_name') : (data?.name || user.name)}
              </div>
              {isGuest ? (
                <div className="cabinet-page-role">{t(lang, 'cab_guest_role')}</div>
              ) : data?.category ? (
                <div className="cabinet-page-role">
                  {data.category}{data.department ? ` · ${data.department}` : ''}
                </div>
              ) : null}
            </div>
          </div>

          {/* Статистика — тільки для авторизованих */}
          {!isGuest && data && (
            <div className="cabinet-page-stats">
              <div className="cabinet-page-stat">
                <span className="cabinet-page-stat-val">{data.issued.length}</span>
                <span className="cabinet-page-stat-label">{t(lang, 'cab_on_hands')}</span>
              </div>
              <div className={`cabinet-page-stat${overdueCount > 0 ? ' cabinet-page-stat--danger' : ''}`}>
                <span className="cabinet-page-stat-val">{overdueCount}</span>
                <span className="cabinet-page-stat-label">{t(lang, 'cab_overdue')}</span>
              </div>
            </div>
          )}

          {/* Кнопка повернення + мобільна інфо про секцію + кнопка оновити */}
          <div className="cabinet-page-header-actions">
            {backLabel && onBack && (
              <button className="cabinet-back-btn" onClick={onBack} aria-label={backLabel}>
                <i className="fa-solid fa-arrow-left cabinet-back-btn-icon" />
                <span className="cabinet-back-btn-label">{backLabel}</span>
              </button>
            )}

            {/* Мобільна інфо про поточну секцію (тільки @xs, між кнопками) */}
            {currentNavItem && (
              <div className="cabinet-mobile-bar">
                <i className={currentNavItem.icon} />
                <span className="cabinet-mobile-bar-label">{currentNavItem.label}</span>
                {badges[currentNavItem.key] > 0 && (
                  <span className="cabinet-page-nav-badge">{badges[currentNavItem.key]}</span>
                )}
              </div>
            )}

            {!isGuest && (
              <Button variant="outline-secondary" size="sm"
              onClick={refreshData} disabled={loading}
                aria-label={t(lang, 'cab_refresh')}
                className="cabinet-refresh-btn ms-auto">
                {loading
                  ? <><Spinner size="sm" animation="border" className="me-1" /><span className="cabinet-refresh-btn-label">...</span></>
                  : <><i className="fa-solid fa-rotate-right me-1 cabinet-refresh-btn-icon" /><span className="cabinet-refresh-btn-label">{t(lang, 'cab_refresh')}</span></>}
              </Button>
            )}
          </div>
        </div>

        {/* Таби навігації */}
        <nav className="cabinet-page-nav" aria-label={t(lang, 'cab_nav_aria')}>
          {activeNavItems.map(item => {
            const isActive = activeSection === item.key;
            const badge    = badges[item.key];
            return (
              <button key={item.key}
                className={`cabinet-page-nav-tab${isActive ? ' active' : ''}`}
                onClick={() => handleSectionChange(item.key)}>
                <i className={item.icon} />
                <span>{item.label}</span>
                {badge > 0 && (
                  <span className="cabinet-page-nav-badge">{badge}</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Тіло секції ── */}
      <div className="cabinet-page-body">

        {/* ── Мобільна сітка секцій (тільки @xs, завжди видима) ── */}
        <div className="cabinet-mobile-tiles">
          {activeNavItems.map(item => {
            const badge = badges[item.key];
            return (
              <button key={item.key}
                className={`cabinet-mobile-tile${activeSection === item.key ? ' cabinet-mobile-tile--active' : ''}`}
                onClick={() => handleSectionChange(item.key)}>
                <i className={item.icon} />
                <span className="cabinet-mobile-tile-label">{item.label}</span>
                {badge > 0 && (
                  <span className="cabinet-mobile-tile-badge">{badge}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Інформаційна плашка для гостей */}
        {isGuest && (
          <div className="cabinet-guest-notice">
            <i className="fa-solid fa-circle-info flex-shrink-0" />
            <span>{t(lang, 'cab_guest_notice')}</span>
          </div>
        )}

        {/* Плашка сесії для гостей (замість TTL-плашки) */}
        {isGuest && (
          <div className="cabinet-guest-session-notice">
            <i className="fa-solid fa-hourglass-half flex-shrink-0" />
            <span>
              {t(lang, 'cab_guest_session_pre')}{' '}
              <strong>{t(lang, 'cab_guest_session_bold')}</strong>
              {t(lang, 'cab_guest_session_post')}
            </span>
          </div>
        )}

        {!isGuest && activeSection === 'issued' && overdueCount > 0 && (
          <Alert variant="danger" className="d-flex align-items-center gap-2 mb-3">
  <i className="fa-solid fa-triangle-exclamation flex-shrink-0 fs-5" />
  <div>
    <strong>{t(lang, 'cab_alert_overdue_title')}</strong>{' '}
    {t(lang, 'cab_alert_overdue_text')}
  </div>
</Alert>
        )}

        <Card className="shadow-sm border-0">

          {/* Скелетон завантаження */}
          {!isGuest && loading && (
            <div className="p-3">
              <div className="cabinet-loading-text">
                <Spinner animation="border" size="sm" className="cabinet-loading-spinner" />
                <span>{t(lang, 'cab_loading_data')}</span>
              </div>
              {[1, 2, 3].map(i => (
                <div key={i} className="cabinet-skel-row px-4 py-3 mb-1">
                  <div className="skeleton cabinet-skel-title" />
                  <div className="skeleton cabinet-skel-line" />
                  <div className="skeleton cabinet-skel-line cabinet-skel-line--short" />
                </div>
              ))}
            </div>
          )}

          {!isGuest && error && !loading && (
            <div className="p-4">
              <Alert variant="danger" className="d-flex align-items-center gap-2">
                <i className="fa-solid fa-triangle-exclamation flex-shrink-0" />{error}
              </Alert>
            </div>
          )}

          {/* Секції авторизованого читача */}
          {!isGuest && !loading && !error && data && (
            <>
              {activeSection === 'issued'    && <SectionIssued    books={data.issued}  onOpenBook={onOpenBook} lang={lang} />}
              {activeSection === 'history'   && <SectionHistory   books={data.history} onOpenBook={onOpenBook} lang={lang} />}
              {activeSection === 'visits'    && <SectionVisits    visits={data.visits} lang={lang} />}
            </>
          )}

          {/* Замовлення — власне джерело даних (order.php), не залежить від cabinet.php */}
          {!isGuest && activeSection === 'orders' && (
            <SectionOrders login={effectiveLogin} user={user} onOpenBook={onOpenBook} lang={lang} refreshTrigger={ordersRefreshTick} />
          )}

          {/* Закладки і запити — доступні й гостям */}
          {activeSection === 'fulltexts' && (
            <SectionFulltexts login={effectiveLogin} onOpenBook={onOpenBook} user={user} lang={lang} />
          )}
          {activeSection === 'bookmarks' && (
            <SectionBookmarks login={effectiveLogin} onOpenBook={onOpenBook} user={user} lang={lang} trackEvent={trackEvent}
              onOrderPlaced={onOrderPlaced} onNavigateSection={handleSectionChange} />
          )}
          {activeSection === 'queries' && (
            <SectionQueries login={effectiveLogin} onRepeatSearch={onRepeatSearch} user={user} lang={lang} trackEvent={trackEvent} />
          )}
        </Card>
      </div>
    </div>
  );
}

export default CabinetPage;
export { getBookmarks, addBookmark, addBookmarksMany, removeBookmark, clearBookmarks, getQueries, getFulltexts, saveFulltextEntry, GUEST_LOGIN, _storageFor, _qDedupeKey, fetchCabinetData, fetchOrderList, fetchReaderPhoto };